import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import PropTypes from "prop-types";
import { useParams, useNavigate, useLocation, useOutletContext } from "react-router-dom";
import { FaThumbsUp, FaClock } from "react-icons/fa";
import serverURL from "../../config";

const DEFAULT_VIOLATION_THRESHOLD = 3;
const WARNING_DURATION_MS = 5000;
const VIOLATION_STORAGE_PREFIX = "quizViolationState";

const getViolationStorageKey = (eventId, levelId) =>
  `${VIOLATION_STORAGE_PREFIX}:${eventId}:${levelId || "default"}`;

const readViolationState = (storageKey) => {
  try {
    const rawValue = sessionStorage.getItem(storageKey);
    if (!rawValue) return { count: 0, disqualified: false };

    const parsedValue = JSON.parse(rawValue);
    return {
      count: Number.isInteger(parsedValue?.count) ? parsedValue.count : 0,
      disqualified: Boolean(parsedValue?.disqualified),
    };
  } catch (error) {
    console.error("[Quiz] Failed to read stored violation state:", error);
    return { count: 0, disqualified: false };
  }
};

const persistViolationState = (storageKey, count, disqualified) => {
  sessionStorage.setItem(
    storageKey,
    JSON.stringify({
      count,
      disqualified,
      updatedAt: Date.now(),
    })
  );
};

const clearViolationState = (storageKey) => {
  sessionStorage.removeItem(storageKey);
};

// Uses crypto.getRandomValues() instead of Math.random() to satisfy SonarQube S2245
const secureRandom = () => {
  const arr = new Uint32Array(1);
  crypto.getRandomValues(arr);
  return arr[0] / (0xffffffff + 1);
};

const shuffleArray = (array) => {
  if (!Array.isArray(array)) {
    console.error("Expected an array to shuffle, but got:", array);
    return [];
  }
  return array
    .map((value) => ({ value, sort: secureRandom() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ value }) => value);
};

const mapCategoryQuestions = (category) =>
  category.questions.map((question) => ({
    ...question,
    category_name: category.category_name,
    shuffled_options: shuffleArray(question.additional_data || []),
  }));

const getAuthToken = () =>
  localStorage.getItem("token") || sessionStorage.getItem("token");

const buildInterviewRequestPayload = (eventId, levelId) => ({
  event_id: parseInt(eventId, 10),
  level_id: levelId ? parseInt(levelId, 10) : undefined,
});

const getInterviewStatusUrl = (eventId, levelId) =>
  levelId
    ? `${serverURL}/interviews/status/${eventId}/${levelId}`
    : `${serverURL}/interviews/status/${eventId}`;

const getInterviewQuestionsUrl = (eventId, levelId) =>
  levelId
    ? `${serverURL}/interviews/questions/${eventId}?levelId=${levelId}`
    : `${serverURL}/interviews/questions/${eventId}`;

const applyViolationStatus = ({
  statusData,
  violationStorageKey,
  setViolationCount,
  setViolationThreshold,
  setIsDisqualified,
  setIntegrityError,
}) => {
  if (!statusData) return;

  const nextViolationCount = statusData.violationCount || 0;
  const disqualified = Boolean(statusData.disqualified);

  setViolationCount(nextViolationCount);
  if (statusData.violationThreshold) {
    setViolationThreshold(statusData.violationThreshold);
  }
  setIsDisqualified(disqualified);
  persistViolationState(
    violationStorageKey,
    nextViolationCount,
    disqualified
  );
  setIntegrityError("");
};

const resetViolationStatus = ({
  violationStorageKey,
  setViolationCount,
  setIsDisqualified,
}) => {
  clearViolationState(violationStorageKey);
  setViolationCount(0);
  setIsDisqualified(false);
};

const syncViolationStatus = async ({
  eventId,
  levelId,
  violationStorageKey,
  setViolationCount,
  setViolationThreshold,
  setIsDisqualified,
  setIntegrityError,
}) => {
  try {
    const statusResponse = await axios.get(
      getInterviewStatusUrl(eventId, levelId),
      {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      }
    );

    applyViolationStatus({
      statusData: statusResponse.data,
      violationStorageKey,
      setViolationCount,
      setViolationThreshold,
      setIsDisqualified,
      setIntegrityError,
    });
  } catch (error) {
    console.error("[Quiz] Error fetching violation status:", error);

    if (error.response?.status === 404) {
      resetViolationStatus({
        violationStorageKey,
        setViolationCount,
        setIsDisqualified,
      });
      return;
    }

    setIntegrityError(
      "Tab-switch monitoring could not be synced. Please check your connection."
    );
  }
};

const populateQuizData = (responseData, setQuestions, setEventDetails) => {
  setQuestions(responseData.questions.flatMap(mapCategoryQuestions));
  setEventDetails(responseData.event);
};

const getQuizBootstrapErrorMessage = (status, data) => {
  if (status === 401) {
    return "Your session has expired. Please log in again.";
  }

  if (status === 403 && data?.disqualified) {
    return "You are disqualified for this test. A retest assignment is required to attempt it again.";
  }

  if (status === 403 && data?.completed) {
    return "You have already completed this test and cannot retake it.";
  }

  return (
    data?.message ||
    "Failed to load test questions. Please contact the administrator."
  );
};

const handleQuizBootstrapError = ({
  error,
  requestStage,
  navigate,
  setError,
}) => {
  console.error(
    `[Quiz] Error bootstrapping quiz during ${requestStage}:`,
    error
  );

  const status = error.response?.status;
  setError(getQuizBootstrapErrorMessage(status, error.response?.data));

  if (status === 401) {
    setTimeout(() => {
      navigate("/login");
    }, 2000);
  }
};

const getQuestionAnswerText = (question, answers) => {
  if (question.question_type === "MCQ") {
    const selectedOptionId = answers[question.id];
    return selectedOptionId ? parseInt(selectedOptionId, 10) : null;
  }

  return answers[question.id] || "";
};

const buildSubmitPayload = (eventId, levelId, questions, answers) => ({
  event_id: parseInt(eventId, 10),
  level_id: levelId ? parseInt(levelId, 10) : undefined,
  answers: questions.map((question) => ({
    question_id: question.id,
    answer_text: getQuestionAnswerText(question, answers),
  })),
});

const getFinalStatus = (reason, isDisqualified) =>
  reason === "disqualified" || isDisqualified ? "disqualified" : "completed";

const getSubmitSuccessMessage = (reason, finalStatus, responseMessage) => {
  if (finalStatus === "disqualified") return "";
  if (reason === "timeout") {
    return "Time's up! Your test has been automatically submitted.";
  }

  return (
    responseMessage || "Your responses have been submitted successfully."
  );
};

const clearWarningTimeout = (warningTimeoutRef) => {
  if (warningTimeoutRef.current) {
    clearTimeout(warningTimeoutRef.current);
  }
};

const hasAnswerValue = (answer) =>
  answer !== undefined && answer !== null && answer !== "";

const hasAnsweredQuestion = (questionId, answers) =>
  hasAnswerValue(answers[questionId]);

const updateQuestionStatus = (
  setQuestionStatus,
  currentQuestionIndex,
  nextStatus
) => {
  setQuestionStatus((prev) => {
    const next = [...prev];
    next[currentQuestionIndex] = nextStatus;
    return next;
  });
};

const moveToNextQuestion = ({
  currentQuestionIndex,
  questions,
  answers,
  setQuestionStatus,
  setCurrentQuestionIndex,
}) => {
  if (currentQuestionIndex >= questions.length - 1) return;

  const currentQuestion = questions[currentQuestionIndex];
  if (!hasAnsweredQuestion(currentQuestion.id, answers)) {
    updateQuestionStatus(setQuestionStatus, currentQuestionIndex, "skipped");
  }

  setCurrentQuestionIndex(currentQuestionIndex + 1);
};

const moveToPreviousQuestion = (
  currentQuestionIndex,
  setCurrentQuestionIndex
) => {
  if (currentQuestionIndex <= 0) return;
  setCurrentQuestionIndex(currentQuestionIndex - 1);
};

const finishQuiz = (isSubmitting, submitTest) => {
  if (!isSubmitting) {
    submitTest("manual");
  }
};

const shouldShowConsentModal = ({
  hasConsented,
  error,
  successMessage,
  isDisqualified,
}) => !hasConsented && !error && !successMessage && !isDisqualified;

const getQuizLayoutClasses = (embedded) => ({
  container: embedded
    ? "flex flex-col h-full bg-white"
    : "min-h-screen flex flex-col bg-campus-bg",
  main: embedded
    ? "flex-1 w-full py-4 px-4 lg:px-6"
    : "flex-1 container mx-auto px-4 py-6 md:py-8",
  content: `flex flex-col gap-6 lg:flex-row`,
  questionSection: embedded
    ? "flex-1 bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col"
    : "flex-1 bg-white border border-campus-border rounded-card shadow-card overflow-hidden",
  questionBody: `overflow-y-auto pr-2 ${
    embedded ? "max-h-[60vh] xl:max-h-[65vh]" : "max-h-[450px]"
  }`,
  sidebar: `${
    embedded ? "w-full lg:w-64 xl:w-72 2xl:w-80 rounded-xl" : "w-full lg:w-72 xl:w-80 rounded-card"
  } bg-white border border-campus-border lg:sticky lg:top-24 h-fit`,
  dialogPosition: embedded ? "absolute" : "fixed",
});

const getQuizViewState = ({
  questions,
  answers,
  questionStatus,
  currentQuestionIndex,
  violationCount,
  violationThreshold,
}) => ({
  allQuestionsAnswered:
    questions.length > 0 &&
    questions.every((question) => hasAnswerValue(answers[question.id])),
  currentQuestion: questions[currentQuestionIndex],
  answeredQuestionsCount: questionStatus.filter((status) => status === "answered")
    .length,
  remainingViolations: Math.max(violationThreshold - violationCount, 0),
  warningNumber: Math.min(
    violationCount,
    Math.max(violationThreshold - 1, 1)
  ),
});

const getWarningPluralSuffix = (remainingViolations) =>
  remainingViolations === 1 ? "" : "s";

const getOptionContainerClasses = (isSelected) =>
  `flex items-center space-x-3 p-4 rounded-card transition-all duration-200 cursor-pointer ${
    isSelected
      ? "bg-blue-100 border-campus-primary shadow-sm"
      : "bg-gray-50 border-campus-border hover:border-campus-primary/30 hover:shadow-sm"
  }`;

const getFinishButtonClasses = (allQuestionsAnswered) =>
  `px-5 py-2.5 rounded-pill font-semibold transition-all text-sm md:text-base w-full sm:w-auto ${
    allQuestionsAnswered
      ? "bg-campus-success hover:scale-105 hover:shadow-glow text-white cursor-pointer"
      : "bg-gray-200 text-gray-400 cursor-not-allowed"
  }`;

const getTimerBadgeClasses = (timer) =>
  `border px-3 py-1.5 rounded-pill flex items-center gap-1.5 shadow-sm transition-all ${
    timer <= 300 && timer > 0
      ? "bg-red-100 border-red-500 text-red-700 animate-pulse"
      : "bg-red-50 border-campus-primary text-campus-primary"
  }`;

const getNavigatorButtonClasses = (status, isCurrent) => `
  w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg font-semibold transition-all border-2 text-sm
  ${status === "answered" ? "bg-campus-success text-white border-campus-success" : ""}
  ${status === "skipped" ? "bg-yellow-400 text-white border-yellow-400" : ""}
  ${status === "not-attended" ? "bg-gray-100 text-campus-text-body border-campus-border" : ""}
  ${isCurrent ? "ring-2 ring-campus-primary scale-110" : ""}
  hover:opacity-80 hover:scale-105
`;

const getDialogClasses = (dialogPositionClass) =>
  `fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 w-full h-full max-w-full max-h-full m-0 border-0 bg-transparent ${dialogPositionClass}`;

const applyViolationResponse = ({
  responseData,
  violationStorageKey,
  warningTimeoutRef,
  setShowWarning,
  setViolationCount,
  setViolationThreshold,
  setIsDisqualified,
  setIntegrityError,
  submitTestRef,
}) => {
  const {
    violationCount: newCount,
    disqualified,
    violationThreshold: threshold,
  } = responseData;

  setViolationCount(newCount);
  if (threshold) {
    setViolationThreshold(threshold);
  }
  persistViolationState(violationStorageKey, newCount, disqualified);
  setIntegrityError("");

  if (disqualified) {
    clearWarningTimeout(warningTimeoutRef);
    setShowWarning(false);
    setIsDisqualified(true);
    submitTestRef.current("disqualified");
    return;
  }

  setShowWarning(true);
  clearWarningTimeout(warningTimeoutRef);
  warningTimeoutRef.current = setTimeout(() => {
    setShowWarning(false);
  }, WARNING_DURATION_MS);
};

const recordViolation = async ({
  eventId,
  levelId,
  violationStorageKey,
  warningTimeoutRef,
  setShowWarning,
  setViolationCount,
  setViolationThreshold,
  setIsDisqualified,
  setIntegrityError,
  submitTestRef,
}) => {
  try {
    const response = await axios.post(
      `${serverURL}/interviews/violation`,
      buildInterviewRequestPayload(eventId, levelId),
      { headers: { Authorization: `Bearer ${getAuthToken()}` } }
    );

    applyViolationResponse({
      responseData: response.data,
      violationStorageKey,
      warningTimeoutRef,
      setShowWarning,
      setViolationCount,
      setViolationThreshold,
      setIsDisqualified,
      setIntegrityError,
      submitTestRef,
    });
  } catch (error) {
    console.error("Violation logging failed", error);
    setIntegrityError(
      "Tab-switch monitoring could not be synced. Please check your connection."
    );
  }
};

const ConsentModal = ({ onCancel, onConfirm }) =>
  createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-75 p-4 backdrop-blur-sm"
      style={{ top: 0, left: 0, right: 0, bottom: 0, width: "100vw", height: "100vh", zIndex: 9999 }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-slide-in relative flex flex-col max-h-[90vh]">
        <div className="bg-gradient-to-r from-gray-700 to-gray-700 p-5 flex-shrink-0">
          <h2 className="text-xl md:text-2xl font-bold text-white flex items-center gap-2">
            ⚠️ Strict Assessment Rules
          </h2>
        </div>
        <div className="p-6 md:p-8 overflow-y-auto">
          <p className="text-gray-700 mb-6 font-medium text-base">
            To ensure fairness, this assessment is strictly monitored. Please read the following rules before proceeding:
          </p>
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-8 rounded-r-lg">
            <ul className="list-disc pl-5 text-red-900 text-sm md:text-base space-y-3 font-medium">
              <li><strong>Do not switch tabs</strong> or leave the active test window.</li>
              <li>You have a maximum of <strong>{DEFAULT_VIOLATION_THRESHOLD} warnings</strong>. On the {DEFAULT_VIOLATION_THRESHOLD + 1}th attempt, you will be automatically <strong>disqualified</strong>.</li>
              <li>Once the test starts, you must complete it. <strong>You cannot go back</strong> or pause the timer.</li>
            </ul>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-end mt-4">
            <button
              onClick={onCancel}
              className="px-6 py-2.5 text-gray-700 font-semibold border-2 border-gray-200 hover:bg-gray-50 rounded-lg transition-colors w-full sm:w-auto text-center"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-6 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white font-bold rounded-lg shadow-md hover:shadow-lg hover:from-red-700 hover:to-red-800 transition-all w-full sm:w-auto text-center"
            >
              Start Test
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );

ConsentModal.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

const QuizAlerts = ({
  showWarning,
  isDisqualified,
  warningNumber,
  violationCount,
  violationThreshold,
  remainingViolations,
  integrityError,
  error,
}) => (
  <>
    {showWarning && !isDisqualified && (
      <div
        className="bg-red-50 border-2 border-red-500 text-red-700 px-4 py-3 rounded-card mb-6 shadow-card animate-slide-in relative"
        role="alert"
        aria-live="assertive"
      >
        <strong className="font-bold">Warning {warningNumber}! </strong>
        <span className="block sm:inline ml-2">
          You left the assessment window. Violation {violationCount} of{" "}
          {violationThreshold}. {remainingViolations} attempt
          {getWarningPluralSuffix(remainingViolations)} remaining before
          automatic disqualification.
        </span>
      </div>
    )}

    {integrityError && (
      <div
        className="bg-amber-50 border-2 border-amber-400 text-amber-800 px-4 py-3 rounded-card mb-6 shadow-card"
        role="alert"
      >
        <p className="font-medium">{integrityError}</p>
      </div>
    )}

    {error && (
      <div
        className="bg-red-50 border-2 border-campus-error text-campus-error px-4 py-3 rounded-card mb-6 shadow-card"
        role="alert"
      >
        <p className="font-medium">{error}</p>
      </div>
    )}
  </>
);

QuizAlerts.propTypes = {
  showWarning: PropTypes.bool.isRequired,
  isDisqualified: PropTypes.bool.isRequired,
  warningNumber: PropTypes.number.isRequired,
  violationCount: PropTypes.number.isRequired,
  violationThreshold: PropTypes.number.isRequired,
  remainingViolations: PropTypes.number.isRequired,
  integrityError: PropTypes.string.isRequired,
  error: PropTypes.string.isRequired,
};

const SuccessDialog = ({
  successMessage,
  isDisqualified,
  dialogPositionClass,
  embedded,
}) => {
  if (!successMessage || isDisqualified) return null;

  return (
    <dialog
      open
      aria-modal="true"
      className={getDialogClasses(dialogPositionClass)}
      aria-labelledby="success-title"
    >
      <div className="bg-white border border-campus-border p-6 md:p-8 rounded-card shadow-hover max-w-md w-full animate-slide-in">
        <div className="text-center">
          <div className="w-16 h-16 md:w-20 md:h-20 bg-campus-success rounded-full flex items-center justify-center mx-auto mb-4">
            <FaThumbsUp className="text-2xl md:text-3xl text-white" />
          </div>
          <h3
            id="success-title"
            className="text-campus-text-primary text-xl md:text-2xl font-heading font-bold mb-2"
          >
            Success!
          </h3>
          <p className="text-campus-text-body">{successMessage}</p>
          {!embedded && (
            <p className="text-campus-text-body/60 text-sm mt-3">
              Redirecting to events...
            </p>
          )}
        </div>
      </div>
    </dialog>
  );
};

SuccessDialog.propTypes = {
  successMessage: PropTypes.string.isRequired,
  isDisqualified: PropTypes.bool.isRequired,
  dialogPositionClass: PropTypes.string.isRequired,
  embedded: PropTypes.bool.isRequired,
};

const DisqualificationDialog = ({
  isDisqualified,
  dialogPositionClass,
  embedded,
  violationThreshold,
}) => {
  if (!isDisqualified) return null;

  return (
    <dialog
      open
      aria-modal="true"
      className={getDialogClasses(dialogPositionClass)}
      aria-labelledby="disqualified-title"
    >
      <div className="bg-white border border-red-500 p-6 md:p-8 rounded-card shadow-hover max-w-md w-full animate-slide-in">
        <div className="text-center">
          <div className="w-16 h-16 md:w-20 md:h-20 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl text-white font-bold">!</span>
          </div>
          <h3
            id="disqualified-title"
            className="text-red-600 text-xl md:text-2xl font-heading font-bold mb-2"
          >
            Disqualified!
          </h3>
          <p className="text-campus-text-body mt-2 font-medium">
            You have exceeded the maximum number of allowed tab switches or lost
            focus ({violationThreshold} violations).
          </p>
          <p className="text-campus-text-body mt-2">
            Your test has been automatically submitted and terminated.
          </p>
          {!embedded && (
            <p className="text-campus-text-body/60 text-sm mt-4">
              Redirecting to events...
            </p>
          )}
        </div>
      </div>
    </dialog>
  );
};

DisqualificationDialog.propTypes = {
  isDisqualified: PropTypes.bool.isRequired,
  dialogPositionClass: PropTypes.string.isRequired,
  embedded: PropTypes.bool.isRequired,
  violationThreshold: PropTypes.number.isRequired,
};

const Quiz = ({ embedded = false, levelId: propLevelId, onComplete }) => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const levelId = propLevelId || location.state?.levelId;
  const violationStorageKey = getViolationStorageKey(eventId, levelId);
  const persistedViolationState = readViolationState(violationStorageKey);
  const [questions, setQuestions] = useState([]);
  const [eventDetails, setEventDetails] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timer, setTimer] = useState(0);

  const { setCustomTitle } = useOutletContext() || {};

  useEffect(() => {
    const title = eventDetails?.name || eventDetails?.event_name;
    if (title && setCustomTitle) {
      setCustomTitle(title);
    }
  }, [eventDetails?.name, eventDetails?.event_name, setCustomTitle]);
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [testStarted, setTestStarted] = useState(false);
  const [questionStatus, setQuestionStatus] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [violationCount, setViolationCount] = useState(persistedViolationState.count);
  const [violationThreshold, setViolationThreshold] = useState(DEFAULT_VIOLATION_THRESHOLD);
  const [isDisqualified, setIsDisqualified] = useState(
    persistedViolationState.disqualified
  );
  const [showWarning, setShowWarning] = useState(false);
  const [integrityError, setIntegrityError] = useState("");
  const consentStorageKey = `testConsented_${eventId}_${levelId || 'default'}`;
  const [hasConsented, setHasConsented] = useState(() => {
    return sessionStorage.getItem(consentStorageKey) === 'true';
  });

  // Track anti-cheat as soon as the candidate can see questions.
  const [questionsLoaded, setQuestionsLoaded] = useState(false);
  const violationCountRef = useRef(violationCount);
  const violationThresholdRef = useRef(violationThreshold);
  const isDisqualifiedRef = useRef(isDisqualified);
  const warningTimeoutRef = useRef(null);
  const violationTimeoutRef = useRef(null);
  const lastViolationAtRef = useRef(0);

  useEffect(() => {
    const storedState = readViolationState(violationStorageKey);
    setViolationCount(storedState.count);
    setIsDisqualified(storedState.disqualified);
    setShowWarning(false);
    setIntegrityError("");
  }, [violationStorageKey]);

  useEffect(() => {
    violationCountRef.current = violationCount;
    persistViolationState(violationStorageKey, violationCount, isDisqualified);
  }, [violationCount, isDisqualified, violationStorageKey]);

  useEffect(() => {
    violationThresholdRef.current = violationThreshold;
  }, [violationThreshold]);

  useEffect(() => {
    isDisqualifiedRef.current = isDisqualified;
  }, [isDisqualified]);

  useEffect(
    () => () => {
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (violationTimeoutRef.current) clearTimeout(violationTimeoutRef.current);
    },
    []
  );

  useEffect(() => {
    if (questions.length > 0) {
      setQuestionStatus(Array(questions.length).fill("not-attended"));
      setQuestionsLoaded(true);
    }
  }, [questions]);

  useEffect(() => {
    if (eventDetails.duration && !testStarted) {
      setTimer(eventDetails.duration * 60);
      setTestStarted(true);
    }
  }, [eventDetails.duration, testStarted]);

  useEffect(() => {
    if (!hasConsented) return;

    const fetchQuestions = async () => {
      let requestStage = "start-test";

      try {
        const token = getAuthToken();

        await axios.post(
          `${serverURL}/interviews/start-test`,
          buildInterviewRequestPayload(eventId, levelId),
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        await syncViolationStatus({
          eventId,
          levelId,
          violationStorageKey,
          setViolationCount,
          setViolationThreshold,
          setIsDisqualified,
          setIntegrityError,
        });

        requestStage = "questions";
        const response = await axios.get(
          getInterviewQuestionsUrl(eventId, levelId),
          {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true,
          }
        );

        populateQuizData(response.data, setQuestions, setEventDetails);
      } catch (err) {
        handleQuizBootstrapError({
          error: err,
          requestStage,
          navigate,
          setError,
        });
      }
    };

    fetchQuestions();
  }, [eventId, levelId, navigate, violationStorageKey, hasConsented]);

  const submitTest = useCallback(
    async (reason) => {
      if (isSubmitting) return;
      setIsSubmitting(true);

      try {
        const token = getAuthToken();
        const response = await axios.post(
          `${serverURL}/interviews/submit-test`,
          buildSubmitPayload(eventId, levelId, questions, answers),
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const finalStatus = getFinalStatus(
          reason,
          isDisqualifiedRef.current
        );
        const successText = getSubmitSuccessMessage(
          reason,
          finalStatus,
          response.data.message
        );

        if (successText) {
          setSuccessMessage(successText);
        }

        clearViolationState(violationStorageKey);
        setIntegrityError("");

        if (embedded && onComplete) {
          setTimeout(() => {
            onComplete({ finalStatus });
          }, 2000);
        }
      } catch (err) {
        const errorMsg = err.response?.data?.message || "Failed to submit your responses.";
        console.error("[Quiz] Submit error:", {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status
        });
        if (!isDisqualified) {
          setError(errorMsg);
        }
        setIsSubmitting(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isSubmitting,
      eventId,
      questions,
      answers,
      levelId,
      embedded,
      onComplete,
      isDisqualified,
      violationStorageKey,
    ]
  );

  const submitTestRef = useRef(submitTest);
  useEffect(() => {
    submitTestRef.current = submitTest;
  }, [submitTest]);

  useEffect(() => {
    let interval;
    if (testStarted && timer > 0 && !isSubmitting) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            submitTestRef.current("timeout");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [testStarted, timer, isSubmitting]);

  const handleViolation = useCallback(async () => {
    if (isSubmitting || isDisqualified || !questionsLoaded) return;

    await recordViolation({
      eventId,
      levelId,
      violationStorageKey,
      warningTimeoutRef,
      setShowWarning,
      setViolationCount,
      setViolationThreshold,
      setIsDisqualified,
      setIntegrityError,
      submitTestRef,
    });
  }, [
    isSubmitting,
    isDisqualified,
    questionsLoaded,
    eventId,
    levelId,
    violationStorageKey,
  ]);

  const handleViolationRef = useRef(handleViolation);
  useEffect(() => {
    handleViolationRef.current = handleViolation;
  }, [handleViolation]);

  useEffect(() => {
    if (!questionsLoaded || isSubmitting || isDisqualified) return;

    const handleBeforeUnload = () => {
      const nextCount = violationCountRef.current + 1;
      const nextDisqualified = nextCount >= violationThresholdRef.current;
      persistViolationState(violationStorageKey, nextCount, nextDisqualified);

      const payload = JSON.stringify(
        buildInterviewRequestPayload(eventId, levelId)
      );
      navigator.sendBeacon(
        `${serverURL}/interviews/violation`,
        new Blob([payload], { type: "application/json" })
      );
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [questionsLoaded, isSubmitting, isDisqualified, eventId, levelId, violationStorageKey]);

  useEffect(() => {
    if (!questionsLoaded || isSubmitting || isDisqualified) return;

    const triggerViolation = () => {
      const pageHidden =
        document.visibilityState === "hidden" || document.hidden;
      const lostFocus = !document.hasFocus();
      const now = Date.now();

      if (!pageHidden && !lostFocus) return;
      if (now - lastViolationAtRef.current > 1000) {
        lastViolationAtRef.current = now;
        if (handleViolationRef.current) {
          handleViolationRef.current();
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) triggerViolation();
    };

    const handleBlur = () => {
      triggerViolation();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
    };
  }, [questionsLoaded, isSubmitting, isDisqualified]);

  useEffect(() => {
    if (testStarted && !isSubmitting && !isDisqualified) {
      window.history.pushState(null, null, window.location.href);
      const handlePopState = (event) => {
        window.history.go(1);
      };
      window.addEventListener("popstate", handlePopState);
      return () => {
        window.removeEventListener("popstate", handlePopState);
      };
    }
  }, [testStarted, isSubmitting, isDisqualified]);

  useEffect(() => {
    if ((successMessage || isDisqualified) && !embedded) {
      const redirectTimer = setTimeout(() => {
        navigate("/events");
      }, 3000);
      return () => clearTimeout(redirectTimer);
    }
  }, [successMessage, isDisqualified, navigate, embedded]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const handleOptionChange = (questionId, answer) => {
    setAnswers((prev) => ({ ...prev, [questionId]: answer }));
    updateQuestionStatus(setQuestionStatus, currentQuestionIndex, "answered");
  };

  const {
    allQuestionsAnswered,
    currentQuestion,
    answeredQuestionsCount,
    remainingViolations,
    warningNumber,
  } = getQuizViewState({
    questions,
    answers,
    questionStatus,
    currentQuestionIndex,
    violationCount,
    violationThreshold,
  });
  const layoutClasses = getQuizLayoutClasses(embedded);

  const handleConsent = () => {
    sessionStorage.setItem(consentStorageKey, 'true');
    setHasConsented(true);
  };

  if (
    shouldShowConsentModal({
      hasConsented,
      error,
      successMessage,
      isDisqualified,
    })
  ) {
    return (
      <ConsentModal
        onCancel={() => navigate("/events")}
        onConfirm={handleConsent}
      />
    );
  }

  return (
    <div className={layoutClasses.container}>
      {!embedded && (
        <div className="bg-white border-b border-campus-border sticky top-0 z-20 shadow-sm">
          {/* header content */}
        </div>
      )}

      <main className={layoutClasses.main}>
        <QuizAlerts
          showWarning={showWarning}
          isDisqualified={isDisqualified}
          warningNumber={warningNumber}
          violationCount={violationCount}
          violationThreshold={violationThreshold}
          remainingViolations={remainingViolations}
          integrityError={integrityError}
          error={error}
        />

        <div className={layoutClasses.content}>
          <section className={layoutClasses.questionSection}>
            <div className="p-5 md:p-6 flex-1 overflow-y-auto custom-scrollbar">
              {questions.length > 0 && currentQuestion ? (
                <>
                  <div className="mb-6">
                    <h2 className="text-xl md:text-2xl font-heading font-semibold text-campus-text-primary mb-2">
                      Question {currentQuestionIndex + 1}
                      <span className="text-campus-text-body text-sm md:text-base font-normal ml-2">
                        ({currentQuestion.category_name})
                      </span>
                    </h2>
                  </div>

                  <div
                    className={layoutClasses.questionBody}
                  >
                    <p className="mb-6 text-campus-text-body leading-relaxed text-base">
                      {currentQuestion.question_text}
                    </p>

                    {currentQuestion.question_type === "MCQ" && (
                      <div className="space-y-3">
                        {currentQuestion.shuffled_options.map((option) => (
                          <label
                            key={option.id}
                            htmlFor={`option-${option.id}`}
                            className={getOptionContainerClasses(
                              answers[currentQuestion.id] === option.id
                            )}
                          >
                            <input
                              type="radio"
                              id={`option-${option.id}`}
                              className="w-5 h-5 text-campus-primary accent-campus-primary cursor-pointer flex-shrink-0"
                              name={`question-${currentQuestion.id}`}
                              checked={answers[currentQuestion.id] === option.id}
                              onChange={() =>
                                handleOptionChange(currentQuestion.id, option.id)
                              }
                            />
                            <span className="text-campus-text-body flex-1">
                              {option.option_text}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}

                    {currentQuestion.question_type === "Programming" && (
                      <div className="flex flex-col lg:flex-row gap-6">
                        <div className="flex-1 lg:w-3/5">
                          <textarea
                            id="code-editor"
                            value={answers[currentQuestion.id] || ""}
                            onChange={(e) =>
                              handleOptionChange(
                                currentQuestion.id,
                                e.target.value
                              )
                            }
                            placeholder="Write your code here..."
                            className="w-full p-4 bg-gray-50 border-2 border-campus-border rounded-card text-campus-text-body focus:ring-2 focus:ring-campus-primary focus:border-campus-primary transition-all font-mono text-sm"
                            rows="12"
                          />
                        </div>
                        <div className="lg:w-2/5 space-y-4">
                          <div className="bg-gray-50 border border-campus-border p-4 rounded-card">
                            <h3 className="font-semibold text-campus-text-primary mb-2">
                              Input Format:
                            </h3>
                            <p className="text-campus-text-body text-sm leading-relaxed">
                              {currentQuestion.additional_data?.input_format ||
                                "No input format provided"}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center py-12">
                  <p className="text-campus-text-body">Loading questions...</p>
                </div>
              )}
            </div>

            {questions.length > 0 && (
              <div className="flex flex-wrap justify-between items-center gap-2 px-5 md:px-6 py-4 border-t border-campus-border bg-gray-50 mt-auto">
                <button
                  onClick={() =>
                    moveToPreviousQuestion(
                      currentQuestionIndex,
                      setCurrentQuestionIndex
                    )
                  }
                  disabled={currentQuestionIndex === 0}
                  className="flex-1 sm:flex-none px-4 sm:px-6 py-2.5 bg-white border border-campus-border text-campus-text-primary rounded-pill disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 transition-all font-medium text-sm md:text-base"
                  aria-label="Previous question"
                >
                  Previous
                </button>

                <div className="relative group flex-shrink-0">
                  <button
                    onClick={() => finishQuiz(isSubmitting, submitTest)}
                    disabled={!allQuestionsAnswered}
                    className={getFinishButtonClasses(allQuestionsAnswered)}
                    aria-label="Finish quiz"
                  >
                    Finish Quiz
                  </button>
                  {!allQuestionsAnswered && (
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-card opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap shadow-lg pointer-events-none">
                      Answer all questions to finish
                    </div>
                  )}
                </div>

                <button
                  onClick={() =>
                    moveToNextQuestion({
                      currentQuestionIndex,
                      questions,
                      answers,
                      setQuestionStatus,
                      setCurrentQuestionIndex,
                    })
                  }
                  disabled={currentQuestionIndex === questions.length - 1}
                  className="flex-1 sm:flex-none px-4 sm:px-6 py-2.5 bg-white border border-campus-border text-campus-text-primary rounded-pill disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 transition-all font-medium text-sm md:text-base"
                  aria-label="Next question"
                >
                  Next
                </button>
              </div>
            )}
          </section>

          <aside className={layoutClasses.sidebar}>
            <div className="p-5">
              <div className="border-b border-campus-border pb-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-heading font-semibold text-campus-text-primary">
                    Question Navigator
                  </h3>
                  <div className={getTimerBadgeClasses(timer)}>
                    <FaClock className="flex-shrink-0 text-xs" />
                    <span className="font-semibold text-sm">
                      {formatTime(timer)}
                    </span>
                  </div>
                </div>
                <div className="text-sm text-campus-text-body">
                  <span className="text-campus-primary font-semibold text-base">
                    {answeredQuestionsCount}
                  </span>
                  <span>/{questions.length} Answered</span>
                </div>
              </div>

              <nav
                aria-label="Question navigation"
                className="grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-[450px] overflow-y-auto p-1"
              >
                {questions.map((question, index) => (
                  <button
                    key={question.id}
                    onClick={() => setCurrentQuestionIndex(index)}
                    className={getNavigatorButtonClasses(
                      questionStatus[index],
                      currentQuestionIndex === index
                    )}
                    aria-label={`Go to question ${index + 1}`}
                    aria-current={
                      currentQuestionIndex === index ? "true" : undefined
                    }
                  >
                    {index + 1}
                  </button>
                ))}
              </nav>

              <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-campus-border">
                <div className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 bg-campus-success rounded flex-shrink-0"
                    aria-hidden="true"
                  />
                  <span className="text-sm text-campus-text-body">
                    Answered
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 bg-yellow-400 rounded flex-shrink-0"
                    aria-hidden="true"
                  />
                  <span className="text-sm text-campus-text-body">
                    Skipped
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 bg-gray-100 border border-campus-border rounded flex-shrink-0"
                    aria-hidden="true"
                  />
                  <span className="text-sm text-campus-text-body">
                    Not Attended
                  </span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </main>

      <SuccessDialog
        successMessage={successMessage}
        isDisqualified={isDisqualified}
        dialogPositionClass={layoutClasses.dialogPosition}
        embedded={embedded}
      />

      <DisqualificationDialog
        isDisqualified={isDisqualified}
        dialogPositionClass={layoutClasses.dialogPosition}
        embedded={embedded}
        violationThreshold={violationThreshold}
      />
    </div>
  );
};

Quiz.propTypes = {
  embedded: PropTypes.bool,
  levelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onComplete: PropTypes.func,
};

// Removed Quiz.defaultProps to follow modern React standards (using default parameters in destructured props)

export default Quiz;
